import { type FormEvent, useRef } from 'react'
import { Head, router, useForm, usePage } from '@inertiajs/react'
import type { CompanyPositionShowProps, PositionDetail, Team } from '@/types/company'
import type { SharedPageProps } from '@/types/shared'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { CompanyHeader } from '@/components/Shared/CompanyHeader'
import { CompanyWorkspaceNav } from '@/components/Shared/CompanyWorkspaceNav'
import { InlineErrorBanner } from '@/components/Shared/InlineErrorBanner'
import { TeamCombobox } from '@/components/Shared/TeamCombobox'

type ShowProps = CompanyPositionShowProps

// Type alias (not interface): usePage<T>'s PageProps constraint needs the
// implicit index signature only object-literal types carry.
type ShowPageProps = SharedPageProps & {
  position: PositionDetail
  teams: Team[]
  errors?: Record<string, string>
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`
  }
  const kilobytes = bytes / 1024
  if (kilobytes < 1024) {
    return `${kilobytes.toFixed(0)} KB`
  }
  return `${(kilobytes / 1024).toFixed(1)} MB`
}

export default function CompanyPositionShow({ position, teams }: ShowProps) {
  const { errors: pageErrors } = usePage<ShowPageProps>().props
  const form = useForm({
    title: position.title,
    location: position.location,
    team_name: position.team?.name ?? '',
    problems_being_solved: position.problemsBeingSolved ?? '',
    ideal_fit: position.idealFit ?? '',
  })

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    form.patch(`/company/positions/${position.id}`)
  }

  const jdForm = useForm<{ jd: File | null }>({ jd: null })
  const fileInputRef = useRef<HTMLInputElement>(null)

  function handleJdUpload(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    jdForm.post(`/company/positions/${position.id}/jd`, {
      forceFormData: true,
      onSuccess: () => {
        jdForm.reset()
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      },
    })
  }

  function handleJdRemove() {
    router.delete(`/company/positions/${position.id}/jd`)
  }

  const detailMissing = position.problemsBeingSolved === null && position.idealFit === null

  return (
    <>
      <Head title={`${position.title} — Hyrin`} />

      <div className="min-h-screen bg-muted">
        <CompanyHeader />

        <main className="mx-auto max-w-[720px] px-6 py-[52px]">
          <p className="mb-[14px] font-mono text-[11px] uppercase tracking-eyebrow text-primary">
            Edit position
          </p>
          <h1 className="mb-[14px] font-heading text-[36px] font-bold leading-[1.06] tracking-[-0.025em] text-foreground">
            {position.title}
          </h1>

          <div className="mb-[18px] flex gap-4">
            <a
              href={`/company/positions/${position.id}/board`}
              className="inline-block font-mono text-[12px] text-primary hover:opacity-80"
            >
              Pipeline board →
            </a>
            <a
              href={`/company/positions/${position.id}/enrich`}
              className="inline-block font-mono text-[12px] text-primary hover:opacity-80"
            >
              Enrich this role →
            </a>
          </div>

          <CompanyWorkspaceNav active="positions" />

          <div className="rounded-[16px] bg-background p-8 shadow-sm">
            <InlineErrorBanner message={pageErrors?.base} />

            {detailMissing && (
              <p className="mb-6 rounded-[8px] bg-muted px-4 py-3 font-sans text-[13px] leading-[1.55] text-muted-foreground">
                No detail yet. Describe the problems this hire will solve and who fits — this is
                the signal the matcher works from.
              </p>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              <div className="flex flex-col gap-[6px]">
                <label
                  htmlFor="position-title"
                  className="font-sans text-[13px] font-medium text-foreground"
                >
                  Title
                </label>
                <Input
                  id="position-title"
                  type="text"
                  value={form.data.title}
                  onChange={(e) => form.setData('title', e.target.value)}
                  disabled={form.processing}
                />
                {form.errors.title && (
                  <p className="font-sans text-[12px] text-destructive">{form.errors.title}</p>
                )}
              </div>

              <div className="flex flex-col gap-[6px]">
                <label
                  htmlFor="position-location"
                  className="font-sans text-[13px] font-medium text-foreground"
                >
                  Location
                </label>
                <Input
                  id="position-location"
                  type="text"
                  value={form.data.location}
                  onChange={(e) => form.setData('location', e.target.value)}
                  disabled={form.processing}
                />
                {form.errors.location && (
                  <p className="font-sans text-[12px] text-destructive">
                    {form.errors.location}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-[6px]">
                <label
                  htmlFor="position-team"
                  className="font-sans text-[13px] font-medium text-foreground"
                >
                  Team
                </label>
                <TeamCombobox
                  id="position-team"
                  value={form.data.team_name}
                  teams={teams}
                  disabled={form.processing}
                  onChange={(value) => form.setData('team_name', value)}
                  listId="edit-position-team"
                />
              </div>

              <div className="flex flex-col gap-[6px]">
                <label
                  htmlFor="position-problems"
                  className="font-sans text-[13px] font-medium text-foreground"
                >
                  Problems being solved
                </label>
                <Textarea
                  id="position-problems"
                  value={form.data.problems_being_solved}
                  onChange={(e) => form.setData('problems_being_solved', e.target.value)}
                  placeholder="What will this hire actually work on in the first year?"
                  disabled={form.processing}
                />
              </div>

              <div className="flex flex-col gap-[6px]">
                <label
                  htmlFor="position-ideal-fit"
                  className="font-sans text-[13px] font-medium text-foreground"
                >
                  Ideal fit
                </label>
                <Textarea
                  id="position-ideal-fit"
                  value={form.data.ideal_fit}
                  onChange={(e) => form.setData('ideal_fit', e.target.value)}
                  placeholder="Background, strengths, and working style of the person who thrives here"
                  disabled={form.processing}
                />
              </div>

              <div className="flex justify-end pt-2">
                <Button type="submit" disabled={form.processing}>
                  {form.processing ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </form>

            <div className="mt-8 border-t border-border pt-6">
              <p className="mb-[6px] font-sans text-[13px] font-medium text-foreground">
                Job description
              </p>

              {position.jd ? (
                <div className="flex items-center justify-between gap-4 rounded-[8px] bg-muted px-4 py-3">
                  <div className="flex flex-col">
                    <a
                      href={position.jd.downloadPath}
                      className="font-mono text-[12px] text-primary hover:opacity-80"
                    >
                      {position.jd.filename} ↓
                    </a>
                    <span className="font-sans text-[12px] text-muted-foreground">
                      {formatBytes(position.jd.byteSize)}
                    </span>
                  </div>
                  <Button variant="ghost" onClick={handleJdRemove}>
                    Remove
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleJdUpload} className="flex flex-col gap-[6px]">
                  <p className="font-sans text-[13px] leading-[1.55] text-muted-foreground">
                    Attach an optional JD file (PDF, Markdown, or text — max 10 MB).
                  </p>
                  <div className="flex items-center gap-3">
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.md,.txt"
                      onChange={(e) => jdForm.setData('jd', e.target.files?.[0] ?? null)}
                      disabled={jdForm.processing}
                    />
                    <Button type="submit" disabled={jdForm.processing || !jdForm.data.jd}>
                      {jdForm.processing ? 'Uploading…' : 'Upload JD'}
                    </Button>
                  </div>
                  {jdForm.errors.jd && (
                    <p className="font-sans text-[12px] text-destructive">{jdForm.errors.jd}</p>
                  )}
                </form>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  )
}
